import React, { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { graphql } from 'gatsby'
import _ from 'lodash'
import { FormattedMessage } from 'react-intl'
import Img from 'gatsby-image'
import { connect } from 'react-redux'
import { mediaXsOnly } from '@gymondo/frontend-core/style-vars'
import { useMediaQuery } from '@gymondo/frontend-core/hooks'
import { setConfig } from '../../../service/layout'

import {
    hero,
    iconsConfig,
    sectionsConfig,
    TRANSLATION_KEY_PREFIX,
} from './config/how-it-works.config'
import { InfoCard, Icon } from './component'

import style from './how-it-works.module.scss'

import { Cta, PageHero } from '../../../component'

const renderIcons = () => (
    <div className={style.iconsSection}>
        <h2 className={cn(style.title, 'gym-h2')}>
            <FormattedMessage id={`${TRANSLATION_KEY_PREFIX}.icon.title`} />
        </h2>
        <div className={style.iconsWrapper}>
            {iconsConfig.map(iconConf => (
                <Icon
                    key={iconConf.title}
                    {...iconConf}
                    className={style.icon}
                />
            ))}
        </div>
        <Cta />
    </div>
)

const renderFoodGrid = imgs => (
    <div className={style.foodWrapper}>
        {imgs.map(img => (
            <Img
                key={`grid-${_.get(
                    img,
                    'node.childImageSharp.fluid.originalName'
                )}`}
                className={style.foodItem}
                fluid={_.get(img, 'node.childImageSharp.fluid')}
            />
        ))}
    </div>
)

const renderContent = (cardImgs, foodImgsEdges) => (
    <div className={cn(style.wrapper, 'wrapper')}>
        {sectionsConfig.map((config, index) => (
            <div
                key={config.title}
                className={cn(
                    config.reversedMobile && style.reversedMobile,
                    style.sectionWrapper
                )}
            >
                <InfoCard
                    reverse={config.reversed}
                    reverseMobile={config.reversedMobile}
                    hideImageOnMobile={config.hideImageOnMobile}
                    zoomImageOnMobile={config.zoomImageOnMobile}
                    title={config.title}
                    subtitle={config.subtitle}
                    fluid={cardImgs[index]}
                    imgClassName={
                        config.customImageClassName &&
                        style[config.customImageClassName]
                    }
                    hasCta={config.hasCta}
                    className={config.className}
                    hasDownloadGroup={config.hasDownloadGroup}
                    displayCtaXS={index === 1}
                />
            </div>
        ))}
        {renderFoodGrid(foodImgsEdges)}
    </div>
)

/**
 */
export const HowItWorks = ({ data, setLayoutConfig }) => {
    const isXs = useMediaQuery(mediaXsOnly)
    const headerImg = _.get(data, 'header.edges.0.node.childImageSharp.fluid')

    const images = data.cards.edges.map(
        (cardEdge, index) => data.cards.edges[index].node.childImageSharp.fluid
    )

    const mobileImage = data.mobileCards.edges.map(
        (cardEdge, index) =>
            data.mobileCards.edges[index].node.childImageSharp.fluid
    )

    const cardImgsEdges = !isXs
        ? [...images]
        : [...images.slice(0, 2), mobileImage, ...images.slice(3)]

    const foodImgsEdges = _.get(data, 'food.edges')

    useLayoutEffect(() => {
        setLayoutConfig({
            className: style.layout,
        })
    }, [setLayoutConfig])

    return (
        <>
            <PageHero
                title={hero.title}
                subtitle={hero.subtitle}
                className={style.heroWrapper}
                contentClassName={style.heroContent}
            >
                <div className={style.heroImageWrapper}>
                    <Img fluid={headerImg} className={style.heroImage} />
                </div>
            </PageHero>
            {renderContent(cardImgsEdges, foodImgsEdges)}
            {renderIcons()}
        </>
    )
}

HowItWorks.propTypes = {
    data: PropTypes.object.isRequired,
    setLayoutConfig: PropTypes.func.isRequired,
}

export default connect(null, { setLayoutConfig: setConfig })(HowItWorks)

export const query = graphql`
    query($locale: String!) {
        header: allFile(
            filter: {
                relativePath: {
                    eq: "page/generic/how-it-works/static/image/header.png"
                }
            }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 1000
                            srcSetBreakpoints: [832]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            ...GatsbyImageSharpFluid_withWebp_noBase64
                        }
                    }
                }
            }
        }
        cards: allFile(
            filter: {
                relativePath: {
                    regex: "page/generic/how-it-works/static/image/card/"
                }
                name: { in: [$locale, "default"] }
            }
            sort: { fields: [relativeDirectory], order: ASC }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 1000
                            srcSetBreakpoints: [832]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            originalName
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        mobileCards: allFile(
            filter: {
                relativePath: {
                    regex: "page/generic/how-it-works/static/image/card/mobile/"
                }
                name: { in: [$locale, "default"] }
            }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 1000
                            srcSetBreakpoints: [832]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            originalName
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        food: allFile(
            filter: {
                relativePath: {
                    regex: "page/generic/how-it-works/static/image/food/"
                }
            }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 832
                            srcSetBreakpoints: [832]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            originalName
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
    }
`
